import { convertFiletoBase64 } from "csi/utility/htmlUtility";
import { cnl } from "csi/typings";
import _ from "lodash";

export type FontFamily = string;
export type FontStyle = string;
export type FontFileInfo = string;
export type FontFileInfoMap = Record<FontFamily, Record<FontFileName, FontFileInfo>>;
export type FontFileName = string;
export type FontFileUrl = cnl;
export type FontFileSrcMap = Record<FontFamily, Record<FontFileName, FontFileUrl>>;

/**
 * REGISTERED VARIABLE AXIS TAGS
 * --------------------------
 * | Axis tag | Name        |
 * | 'ital' | Italic        |
 * | 'opsz' | Optical size  |
 * | 'slnt' | Slant         |
 * | 'wdth' | Width         |
 * | 'wght' | Weight        |
 * --------------------------
 */
export enum FontAxisTag {
	ITAL = "ital",
	OPSZ = "opsz",
	SLNT = "slnt",
	WDTH = "wdth",
	WGHT = "wght",
};

export interface FontVariationAxis {
	defaultValue: number;
	maxValue: number;
	minValue: number;
	name: {
		en: string;
	};
	tag: FontAxisTag;
};

export interface OperatingSystem2Table {
	usWeightClass: number;
	usWidthClass: number;
}

export interface IFontInfo {
    names: {
        fontFamily: FontFamily;
        fontSubfamily: string;
        preferredFamily: string;
    };
    tables: {
        os2: OperatingSystem2Table;
        fvar: {
            axes: FontVariationAxis;
        };
    };
};

export interface IFontInfoParsed {
	fontFamily: FontFamily;
	fvar: boolean;
	style: string;
	weight: string;
	widths: number[];
};

export const WEIGHT_WORD_MAP = {
	100: ["thin", "Thin"],
	200: ["extra-light", "Extra Light"],
	300: ["light", "Light"],
	400: ["normal", "Regular"],
	500: ["medium", "Medium"],
	600: ["semi-bold", "Semi Bold"],
	700: ["bold", "Bold"],
	800: ["extra-bold", "Extra Bold"], 
	900: ["black", "Black"],
};

const WIDTH_TO_PERCENTAGE_MAP = {
	1: 50,
	2: 62.5,
	3: 75,
	4: 87.5,
	5: 100,
	6: 112.5,
	7: 125,
	8: 150,
	9: 200,
};

export const WIDTH_WORD_MAP = {
	50: ["ultra-condensed", "UltraCondensed"],
	62.5: ["extra-condensed", "ExtraCondensed"],
	75: ["condensed", "Condensed"],
	87.5: ["semi-condensed", "SemiCondensed"],
	100: ["normal", ""],
	112.5: ["semi-expanded", "SemiExpanded"],
	125: ["expanded", "Expanded"],
	150: ["extra-expanded", "ExtraExpanded"], 
	200: ["ultra-expanded", "UltraExpanded"],
};

/**
 * Convert numbered weight to its corresponding common word value (round to nearest hundred).
 */
export function convertWeightToWord(weight: number | string, formatted?: boolean): string {
	if (typeof weight === "string") {
		weight = parseInt(weight);
	}
	const roundedWeight =  Math.round(weight / 100) * 100;
	const formattedWord = formatted ? 1 : 0;
	const word = WEIGHT_WORD_MAP[roundedWeight];
	return word?.[formattedWord] || "";
};

/**
 * Convert numbered width to its corresponding common word value.
 */
export function convertWidthToWord(width: number, formatted?: boolean): string {
	const formattedWord = formatted ? 1 : 0;
	const word = WIDTH_WORD_MAP[width];
	return word?.[formattedWord] || "";
};

/**
 * Convert US font width to its corresponding percentage.
 */
export function convertUsWidthToPercentage(usWidth: number): number {
	return WIDTH_TO_PERCENTAGE_MAP[usWidth];
};

/**
 * Get a string array of font families used in node or html string.
 */
export function getFontFamiliesFromHTML(innerHTML: string, node?: HTMLElement): string[] {
	let contentNode;
	if (node) {
		contentNode = node;
	} else {
		contentNode = document.createElement("div");
		contentNode.innerHTML = innerHTML;
	}
	return _.compact([...contentNode.querySelectorAll("span")].map((span) => span.style.fontFamily));
};

/**
 * Get Variable Family name for a width. Static fonts already have this width.
 */
export function getVariableFontName(fontFamily: string, width: number): string {
	let formattedWidth = convertWidthToWord(width, true);
	if (formattedWidth) {
		formattedWidth = ` ${formattedWidth}`;
	}
	return `${fontFamily}${formattedWidth}`;
};

/**
 * Get variable weights for fonts.
 */
export function getVariableWeights(minMax: string): number[] {
	const weight = {
		minValue: 0,
		maxValue: 0,
	};

	// Handle the scenario where the min and max are given in the same way a variable font is declared
	// in a CSS file. E.g. font-weight: 200 900;
	const [min, max] = minMax.split(" ");
	weight.minValue = parseInt(min);
	weight.maxValue = parseInt(max);

	const weights = [];
	if (weight.minValue === 0 && weight.maxValue === 0) {
		weights.push(400);
	} else if (weight.minValue === weight.maxValue) {
		weights.push(weight.minValue);
	} else {
		let x = weight.minValue;
		while (x <= weight.maxValue) {
			if (x >= weight.minValue && x <= weight.maxValue) {
				weights.push(x);
			}
			x += 100;
		}
	}
	return weights;
};

/** 
 * <---------------- Utilities for parsing fonts  ---------------------->
 * Some functions adapted from: https://github.com/tsayen/dom-to-image
 */

/**
 * Only WOFF and EOT mime types for fonts are 'real'
 * see http://www.iana.org/assignments/media-types/media-types.xhtml
 */
const MIMES = {
	woff: "application/font-woff",
	woff2: "application/font-woff",
	ttf: "application/font-truetype",
	eot: "application/vnd.ms-fontobject",
	png: "image/png",
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	gif: "image/gif",
	tiff: "image/tiff",
	svg: "image/svg+xml",
};

/**
 * Use a URL_MAP to temporarily cache results, as to not fetch them twice.
 */
const URL_MAP: Record<string, string> = {};

// Regex for recognizing src URLs.
const URL_REGEX = /url\(['"]?([^'"]+?)['"]?\)/g;
 
/**
  * Set or get data in the map.
  */
function setOrGetData(key: string, value?: string): string | void {
	  if (!value) {
		  return URL_MAP[key];
	  } else {
		  URL_MAP[key] = value;
	  }
};
 
/**
  * Parse extension of file url.
  */
function parseExtension(url: string): string {
	 const match = /\.([^\.\/]*?)$/g.exec(url);
	 if (match) return match[1];
	 else return "";
};
 
/**
  * Get MIME type of file url.
  */
function mimeType(url: string): string {
	 const extension = parseExtension(url)
		.toLowerCase();
	 return MIMES[extension] || "";
};
 
/**
  * Put together the BaseUrl with Url.
  */
function resolveUrlWithBase(url: string, baseUrl: string): string {
	 const doc = document.implementation.createHTMLDocument();
	 const base = doc.createElement("base");
	 doc.head.appendChild(base);
	 const a = doc.createElement("a");
	 doc.body.appendChild(a);
	 base.href = baseUrl;
	 a.href = url;
	 return a.href;
};
 
/**
  * Get and encode the font url, timing out quickly.
  */
async function getAndEncode(url: string): Promise<string> {
	 const previous = setOrGetData(url);
	 if (previous) { return previous; };
	 let blob;
	 try {
		 const controller = new AbortController();
 
		 // 500 millisecond timeout:
		 const timeoutId = setTimeout(() => controller.abort(), 500);
 
		 blob = await fetch(url, { signal: controller.signal })
			.then((response) => {
			 clearTimeout(timeoutId);
			 return response;
		 });
		 blob = await blob.blob();
	 } catch (err) {
		 console.info(err);
		 return "";
	 }
	 const base64String = await convertFiletoBase64(blob);
	 const trimmed = base64String.split(/,/)[1];
	 setOrGetData(url, trimmed);
	 return trimmed;
};
 
/**
  * Inline string with given url as parameter.
  */
function inline(fontRuleString: string, url: string, baseUrl: string): Promise<string> {
	 return Promise.resolve(url)
		 .then((url) => {
			 return baseUrl ? resolveUrlWithBase(url, baseUrl) : url;
		 })
		 .then(getAndEncode)
		 .then((data) =>  "data:" + mimeType(url) + ";base64," + data)
		 .then((dataUrl) => fontRuleString.replace(urlAsRegex(url), "$1" + dataUrl + "$3"));
 
	 function urlAsRegex(url): RegExp {
		 return new RegExp("(url\\(['\"]?)(" + _.escapeRegExp(url) + ")(['\"]?\\))", "g");
	 }
};

/**
 * Read url from string.
 */
function readUrlsFromRule(fontRuleString: string): string[] {
	const result = [];
	let match;
	while ((match = URL_REGEX.exec(fontRuleString)) !== null) {
		result.push(match[1]);
	}
	return result.filter((url) => url.search(/^(data:)/) === -1);
};
 
/**
  * Inline entire font rule cssText string.
  */
export async function inlineFontRule(fontRuleString: string, baseUrl: string): Promise<string> {
	if (fontRuleString.search(URL_REGEX) === -1) {
		return fontRuleString;
	}

	return Promise.resolve(fontRuleString)
		.then(readUrlsFromRule)
		.then((urls) => {
			let done = Promise.resolve(fontRuleString);
			urls.forEach((url) => {
				done = done.then((s) => {
					return inline(s, url, baseUrl);
				});
			});
			return done;
		});
};